import * as React from 'react'
import { b, O, pipe, RA, RR, S } from '@packages/prelude'
import * as domain from '@packages/domain'
import { Type, starGreetingsGreetingLens, starGreetingsSelectedLens } from '~/components/ProductPreview/state/GreetingField'
import { bindFieldsByParameterId } from '~/components/ProductPreview/state/fieldParams'

export interface UseGreetingFieldsArgs {
  meta: domain.Meta.ProductPersonalization.Type
  parameters: ReadonlyArray<domain.IAParameter.Type>
  initialData: O.Option<domain.Api.IALoadPreviewResponse.Type>
}

export interface UseGreetingFieldsResult {
  starGreeting: RR.ReadonlyRecord<string, Type>
  onTextChange: (id: string) => (text: O.Option<string>) => void
  onSelectToggle: (id: string) => () => void
  reset: () => void
}

// StarGreeting (pers-sprecherwidmung) fields bind by explicit parameterId to the VU param pool.
const pairGreetings = bindFieldsByParameterId('starGreeting', domain.IAParameter.ADT.is.VU)

// Pure derivation of the greeting skeleton from props; reused for lazy init + prop-change re-seed.
const buildGreetings = (
  meta: domain.Meta.ProductPersonalization.Type,
  parameters: ReadonlyArray<domain.IAParameter.Type>,
  initialData: O.Option<domain.Api.IALoadPreviewResponse.Type>
): RR.ReadonlyRecord<string, Type> =>
  pipe(
    pairGreetings(meta, parameters),
    RA.map(([field, param]): Type => {
      const greeting = pipe(initialData, O.chain(domain.Api.IALoadPreviewResponse.parameterById(String(param.id)).getOption))
      return {
        id: param.id,
        fieldId: field.id,
        title: field.label,
        description: param.description,
        optional: param.optional,
        greeting,
        selected: O.isSome(greeting)
      }
    }),
    params => RR.fromFoldableMap(S.last<Type>(), RA.Foldable)(params, p => [String(p.id), p])
  )

export const useGreetingFields = ({ meta, parameters, initialData }: UseGreetingFieldsArgs): UseGreetingFieldsResult => {
  const [starGreeting, setStarGreeting] = React.useState<RR.ReadonlyRecord<string, Type>>(() =>
    buildGreetings(meta, parameters, initialData)
  )

  React.useEffect(() => {
    setStarGreeting(buildGreetings(meta, parameters, initialData))
  }, [meta, parameters, initialData])

  const onTextChange = React.useCallback(
    (id: string) => (text: O.Option<string>) => setStarGreeting(starGreetingsGreetingLens(id).set(text)),
    []
  )

  const onSelectToggle = React.useCallback((id: string) => () => setStarGreeting(starGreetingsSelectedLens(id).modify(b.not)), [])

  const reset = React.useCallback(() => setStarGreeting(RR.map(d => ({ ...d, greeting: O.none, selected: false }))), [])

  return { starGreeting, onTextChange, onSelectToggle, reset }
}
